import { readBooleanEnv } from "./configs/featureFlags";
import { getRuntimeEnv } from "./configs/runtimeConfig";
import { appConfig, currentTenant } from "./configs/tenantConfig";

const ERROR_MESSAGE_MAX_LENGTH = 300;
const BROWSER_HANDLERS_KEY = "__issuerPortalOtelErrorHandlers";

let otelLogger = null;
let severity = null;
let otelTrace = null;
let SpanStatusCode = null;
let tracesEnabledFlag = false;
let telemetryServiceName = "";
let traceProvider = null;
let loggerProvider = null;

function stripTrailingSlash(value) {
  return String(value || "").replace(/\/+$/, "");
}

function buildSignalUrl(baseUrl, signalPath) {
  if (!baseUrl) {
    return "";
  }
  return `${stripTrailingSlash(baseUrl)}${signalPath}`;
}

export function isOtelAlertingEnabled() {
  return readBooleanEnv(getRuntimeEnv("REACT_APP_OTEL_ENABLED"), false);
}

function currentPageAttributes() {
  if (typeof window === "undefined") return {};
  return {
    "page.path": window.location.pathname,
    "page.url": stripUrlToPath(window.location.href),
  };
}

function stripUrlToPath(rawUrl) {
  try {
    const origin =
      typeof window !== "undefined" ? window.location.origin : undefined;
    const parsed = new URL(rawUrl, origin);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return String(rawUrl).split("?")[0] || rawUrl;
  }
}

function truncateErrorMessage(message) {
  return String(message || "").slice(0, ERROR_MESSAGE_MAX_LENGTH);
}

function buildAlertAttributes(message, attributes = {}) {
  const page = currentPageAttributes();
  const endpoint =
    typeof attributes.endpoint === "string" ? attributes.endpoint : "";
  const existingUrl =
    typeof attributes["http.url"] === "string" ? attributes["http.url"] : "";
  const httpUrl = existingUrl || (endpoint ? stripUrlToPath(endpoint) : "");

  return {
    ...attributes,
    ...page,
    path: page["page.path"] ?? "",
    "error.message": truncateErrorMessage(message),
    "error.type": String(
      attributes.event ?? attributes["error.type"] ?? "Error",
    ),
    ...(httpUrl ? { "http.url": stripUrlToPath(httpUrl) } : {}),
  };
}

function applyPageAttributes(span) {
  Object.entries(currentPageAttributes()).forEach(([key, value]) => {
    span.setAttribute(key, value);
  });
}

function fetchResultErrorText(result) {
  if (typeof Response !== "undefined" && result instanceof Response) {
    return result.statusText;
  }
  if (typeof result === "object" && result) {
    return String(result.statusText ?? result.message ?? "");
  }
  return "";
}

function markHttpSpanError(span, status, statusText) {
  if (!status) return;

  // Keep 4xx in traces for debugging, but do not mark them as errors so Discord
  // does not page on user mistakes (wrong password, unknown account, validation).
  if (status < 500) {
    if (SpanStatusCode) {
      span.setStatus({ code: SpanStatusCode.UNSET });
    }
    return;
  }

  span.setAttribute(
    "error.message",
    truncateErrorMessage(statusText || `HTTP ${status}`),
  );
  span.setAttribute("error.type", "http_error");
  if (SpanStatusCode) {
    span.setStatus({
      code: SpanStatusCode.ERROR,
      message: truncateErrorMessage(statusText || `HTTP ${status}`),
    });
  }
}

function applyFetchAlertAttributes(span, request, result) {
  applyPageAttributes(span);

  const method =
    typeof Request !== "undefined" && request instanceof Request
      ? request.method
      : typeof request === "object" && request
        ? (request.method ?? "GET")
        : "GET";
  span.setAttribute("http.method", method);

  const rawUrl =
    typeof request === "string"
      ? request
      : typeof Request !== "undefined" && request instanceof Request
        ? request.url
        : typeof result === "object" && result && typeof result.url === "string"
          ? result.url
          : "";
  if (rawUrl) {
    span.setAttribute("http.url", stripUrlToPath(rawUrl));
  }

  const status =
    typeof Response !== "undefined" && result instanceof Response
      ? result.status
      : typeof result === "object" &&
          result &&
          typeof result.status === "number"
        ? result.status
        : 0;
  if (status) {
    span.setAttribute("http.status_code", status);
  }

  markHttpSpanError(span, status, fetchResultErrorText(result));
}

function applyXhrAlertAttributes(span, xhr) {
  applyPageAttributes(span);
  if (xhr.responseURL) {
    span.setAttribute("http.url", stripUrlToPath(xhr.responseURL));
  }
  if (xhr.status) {
    span.setAttribute("http.status_code", xhr.status);
  }
  markHttpSpanError(span, xhr.status, xhr.statusText);
}

function flushTelemetry() {
  void Promise.all([
    traceProvider?.forceFlush?.(),
    loggerProvider?.forceFlush?.(),
  ]).catch(() => {});
}

function recordErrorSpan(message, attributes = {}) {
  if (!tracesEnabledFlag || !otelTrace || !SpanStatusCode) return;

  const tracer = otelTrace.getTracer(telemetryServiceName || "frontend");
  const span = tracer.startSpan("frontend.error");
  const errorMessage = truncateErrorMessage(message);
  const alertAttributes = buildAlertAttributes(message, attributes);

  span.setAttributes(alertAttributes);
  span.setStatus({ code: SpanStatusCode.ERROR, message: errorMessage });
  span.recordException(new Error(errorMessage));
  span.end();
  flushTelemetry();
}

function registerBrowserErrorHandlers() {
  if (typeof window === "undefined") return;
  if (window[BROWSER_HANDLERS_KEY]) return;
  window[BROWSER_HANDLERS_KEY] = true;

  window.addEventListener("error", (event) => {
    logError(event.message || "Uncaught error", {
      event: "uncaught_error",
      "error.type": event.error?.name ?? "Error",
      "code.filepath": event.filename ?? "",
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const message =
      reason instanceof Error
        ? reason.message
        : String(reason ?? "Unhandled rejection");
    logError(message, {
      event: "unhandled_rejection",
      "error.type":
        reason instanceof Error ? reason.name : "UnhandledRejection",
    });
  });
}

export function logError(message, attributes = {}) {
  if (isOtelAlertingEnabled()) {
    recordErrorSpan(message, attributes);
  }

  if (!otelLogger || !severity) return;

  otelLogger.emit({
    severityNumber: severity.ERROR,
    severityText: "ERROR",
    body: truncateErrorMessage(message),
    attributes: isOtelAlertingEnabled()
      ? {
          environment:
            getRuntimeEnv("REACT_APP_ENV") ||
            getRuntimeEnv("REACT_APP_OTEL_ENV") ||
            "",
          tenant: currentTenant,
          ...buildAlertAttributes(message, attributes),
        }
      : {
          environment:
            getRuntimeEnv("REACT_APP_ENV") ||
            getRuntimeEnv("REACT_APP_OTEL_ENV") ||
            "",
          tenant: currentTenant,
          path: typeof window !== "undefined" ? window.location.pathname : "",
          ...attributes,
        },
  });

  if (isOtelAlertingEnabled()) {
    flushTelemetry();
  }
}

export function isTelemetryActive() {
  return Boolean(otelLogger);
}

export async function initTelemetry() {
  const otelEnabled = readBooleanEnv(
    getRuntimeEnv("REACT_APP_OTEL_ENABLED"),
    false,
  );
  if (!otelEnabled) {
    return;
  }

  const otelUseDevProxy = readBooleanEnv(
    getRuntimeEnv("REACT_APP_OTEL_USE_DEV_PROXY"),
    true,
  );
  const otelBaseUrl = stripTrailingSlash(
    getRuntimeEnv("REACT_APP_OTEL_BASE_URL") ?? "",
  );

  if (!otelBaseUrl) {
    console.warn(
      "[telemetry] REACT_APP_OTEL_ENABLED is true but REACT_APP_OTEL_BASE_URL is empty",
    );
    return;
  }

  const isDev = process.env.NODE_ENV === "development";
  const runtimeOtelBaseUrl = isDev && otelUseDevProxy ? "/otlp" : otelBaseUrl;

  const tracesEnabled = readBooleanEnv(
    getRuntimeEnv("REACT_APP_OTEL_TRACES_ENABLED"),
    true,
  );
  const metricsEnabled = readBooleanEnv(
    getRuntimeEnv("REACT_APP_OTEL_METRICS_ENABLED"),
    false,
  );
  const logsEnabled = readBooleanEnv(
    getRuntimeEnv("REACT_APP_OTEL_LOGS_ENABLED"),
    false,
  );
  const metricsExportIntervalMs = Number(
    getRuntimeEnv("REACT_APP_OTEL_METRICS_EXPORT_INTERVAL_MS") || 15000,
  );
  const serviceName =
    getRuntimeEnv("REACT_APP_OTEL_SERVICE_NAME") || appConfig.otelServiceName;
  const deploymentEnvironment =
    getRuntimeEnv("REACT_APP_OTEL_ENV") ||
    getRuntimeEnv("REACT_APP_ENV") ||
    (isDev ? "local" : "unknown");
  const alertingEnabled = isOtelAlertingEnabled();

  const [
    { metrics, trace, SpanStatusCode: ImportedSpanStatusCode },
    { logs, SeverityNumber },
    { OTLPTraceExporter },
    { registerInstrumentations },
    { FetchInstrumentation },
    { XMLHttpRequestInstrumentation },
    { resourceFromAttributes },
    { BatchSpanProcessor, WebTracerProvider },
    { ATTR_SERVICE_NAME },
  ] = await Promise.all([
    import("@opentelemetry/api"),
    import("@opentelemetry/api-logs"),
    import("@opentelemetry/exporter-trace-otlp-http"),
    import("@opentelemetry/instrumentation"),
    import("@opentelemetry/instrumentation-fetch"),
    import("@opentelemetry/instrumentation-xml-http-request"),
    import("@opentelemetry/resources"),
    import("@opentelemetry/sdk-trace-web"),
    import("@opentelemetry/semantic-conventions"),
  ]);

  severity = SeverityNumber;
  otelTrace = trace;
  SpanStatusCode = ImportedSpanStatusCode;
  telemetryServiceName = serviceName;

  const traceUrl = buildSignalUrl(runtimeOtelBaseUrl, "/v1/traces");
  const metricUrl = buildSignalUrl(runtimeOtelBaseUrl, "/v1/metrics");
  const logUrl = buildSignalUrl(runtimeOtelBaseUrl, "/v1/logs");
  const ignoredTelemetryUrls = [
    traceUrl,
    metricUrl,
    logUrl,
    /\/v1\/traces(?:\?|$)/,
    /\/v1\/metrics(?:\?|$)/,
    /\/v1\/logs(?:\?|$)/,
  ];

  const resource = resourceFromAttributes({
    [ATTR_SERVICE_NAME]: serviceName,
    "deployment.environment": deploymentEnvironment,
    tenant: currentTenant,
  });

  if (tracesEnabled && traceUrl) {
    const traceExporter = new OTLPTraceExporter({ url: traceUrl });
    const tracerProvider = new WebTracerProvider({
      resource,
      spanProcessors: [
        new BatchSpanProcessor(traceExporter, {
          scheduledDelayMillis: 1000,
        }),
      ],
    });
    tracerProvider.register();
    traceProvider = tracerProvider;
    tracesEnabledFlag = true;

    registerInstrumentations({
      instrumentations: [
        new FetchInstrumentation({
          ignoreUrls: ignoredTelemetryUrls,
          clearTimingResources: true,
          ...(alertingEnabled
            ? { applyCustomAttributesOnSpan: applyFetchAlertAttributes }
            : {}),
        }),
        new XMLHttpRequestInstrumentation({
          ignoreUrls: ignoredTelemetryUrls,
          ...(alertingEnabled
            ? { applyCustomAttributesOnSpan: applyXhrAlertAttributes }
            : {}),
        }),
      ],
    });

    const tracer = trace.getTracer(serviceName);
    const bootstrapSpan = tracer.startSpan("frontend.telemetry.bootstrap");
    bootstrapSpan.setAttribute("deployment.environment", deploymentEnvironment);
    bootstrapSpan.end();
    tracerProvider.forceFlush().catch(() => {});
  }

  if (metricsEnabled && metricUrl) {
    const [
      { OTLPMetricExporter },
      { MeterProvider, PeriodicExportingMetricReader },
    ] = await Promise.all([
      import("@opentelemetry/exporter-metrics-otlp-http"),
      import("@opentelemetry/sdk-metrics"),
    ]);
    const metricExporter = new OTLPMetricExporter({ url: metricUrl });
    const meterProvider = new MeterProvider({
      resource,
      readers: [
        new PeriodicExportingMetricReader({
          exporter: metricExporter,
          exportIntervalMillis: Number.isFinite(metricsExportIntervalMs)
            ? metricsExportIntervalMs
            : 15000,
        }),
      ],
    });
    metrics.setGlobalMeterProvider(meterProvider);

    const meter = meterProvider.getMeter(serviceName);
    const pageLoads = meter.createCounter("frontend.page_loads", {
      description: "Number of frontend initializations",
    });
    pageLoads.add(1, {
      "deployment.environment": deploymentEnvironment,
      tenant: currentTenant,
    });
  }

  if (logsEnabled && logUrl) {
    const [{ OTLPLogExporter }, { BatchLogRecordProcessor, LoggerProvider }] =
      await Promise.all([
        import("@opentelemetry/exporter-logs-otlp-http"),
        import("@opentelemetry/sdk-logs"),
      ]);
    const logExporter = new OTLPLogExporter({ url: logUrl });
    const provider = new LoggerProvider({
      resource,
      processors: [
        new BatchLogRecordProcessor({
          exporter: logExporter,
          scheduledDelayMillis: 1000,
        }),
      ],
    });
    logs.setGlobalLoggerProvider(provider);
    loggerProvider = provider;

    otelLogger = provider.getLogger(serviceName);
    otelLogger.emit({
      body: "Frontend telemetry initialized",
      severityNumber: SeverityNumber.INFO,
      severityText: "INFO",
      attributes: {
        "deployment.environment": deploymentEnvironment,
        tenant: currentTenant,
      },
    });
    provider.forceFlush().catch(() => {});
  }

  if (alertingEnabled && (tracesEnabledFlag || otelLogger)) {
    registerBrowserErrorHandlers();
  }
}
